// ---------------------------------------------------------------------------
// Updates an ALREADY OPEN project from a folder of edited CSVs (the same
// files exportCsvProject.ts writes) — unlike importCsvProject.ts (which
// builds a brand-new project, always losing geometry), this MERGES values
// into the existing project by NAME: a sub-basin/zone/aquifer/outlet point
// is matched against its current counterpart by name, and only its VALUES
// change (coefficients, Vo/Alfa, parameters, model, assignments...).
//
// Never touched by this merge, regardless of what the CSVs say:
//   - Geometry/GIS layers, and every internal code (subBasinCode, gisCode,
//     outletCode, aquiferCode, zoneCode) — codes are re-derived from names
//     each time, never read from the CSV (Néstor's own requirement: the
//     "code" columns, i.e. names, can never be used to RENAME anything).
//   - Input series (precipCol/etpCol/tempCol/pumpingCol) and the series
//     data itself — this only updates structural data, not day-by-day
//     values, so Ent_Precip.csv and friends are deliberately never read
//     here even if present in the folder.
//   - Ent_Etiquetas.csv — static, nothing to merge.
//
// What DOES change, matched by name:
//   - Sub-basins (Ent_Cuencas.csv): area, precip/etp/temp adjustment
//     coefficients, partial-results flag, outlet/aquifer assignment, zone/
//     snow-zone assignment (and the model that follows from it). A CSV row
//     whose name doesn't match any EXISTING sub-basin is skipped with a
//     notice — this never CREATES a sub-basin (it would have no geometry in
//     a GIS-linked project, and this operation is about updating values,
//     not growing the project). A sub-basin the CSV doesn't mention at all
//     is left completely untouched.
//   - Zones (Ent_{modelo}_Param.csv): parameter/state values always
//     replaced for a matched zone; if a name isn't found, a NEW zone is
//     created — editing a fresh block into one of these files is a
//     legitimate way to add a zone. Moving a zone's row to a DIFFERENT
//     model's file changes its assigned model, same as reassigning it in
//     the UI.
//   - Aquifers/outlet points (Ent_Acu.csv/Ent_Apo.csv): same idea — update
//     if the name matches, create if it doesn't.
//   - Ent_Config.csv: Unidades and Op_neg_acu/Op_bombeos, only if the file
//     is present (missing file = leave those settings alone, unlike
//     importCsvProject.ts's from-scratch defaults).
// ---------------------------------------------------------------------------
import { MODEL_TYPES } from "./baseTemplate.ts";
import {
  DEFAULT_PATHS,
  LABEL_AQUIFERS,
  LABEL_OUTLET_POINTS,
  LABEL_CONFIG,
  LABEL_SUBBASINS,
  paramFileName,
} from "./csvProjectFileNames.ts";
import {
  csvHeaderIndex,
  numberOr,
  parseAquiferCsvFields,
  parseAquiferOptionFlags,
  parseConfigValues,
  parseOutletPointName,
  parseSimpleCsv,
  parseSubBasinCsvFields,
  parseZoneParamRow,
  readCsvText,
  unitOptionsFromConfigValues,
  zoneParamDefsFor,
} from "./csvProjectParsing.ts";
import { nextId } from "./entityHelpers.ts";
import type { ReadableFolder } from "./fsReadable.ts";
import type { Aquifer, EvalHidProject, OutletPoint, Zone } from "./types.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";

export interface CsvUpdateSummary {
  subBasinsUpdated: number;
  zonesUpdated: number;
  zonesCreated: number;
  aquifersUpdated: number;
  aquifersCreated: number;
  /** Ent_Apo.csv only ever carries a name (see parseOutletPointName) — an existing outlet point has nothing else to update, so only creations are counted. */
  outletsCreated: number;
  configUpdated: boolean;
  /** Anything worth telling the user beyond the counts above — unmatched sub-basin rows, unresolved references, name collisions. */
  notices: string[];
}

interface CsvUpdateResult {
  project: EvalHidProject;
  summary: CsvUpdateSummary;
}

function mergeOutletPoints(
  text: string | null,
  existing: OutletPoint[]
): { outletPoints: OutletPoint[]; created: number } {
  if (text == null) return { outletPoints: existing, created: 0 };
  let outletPoints = existing;
  let created = 0;
  for (const cols of parseSimpleCsv(text)) {
    const name = parseOutletPointName(cols);
    if (!name || outletPoints.some((p) => p.name === name)) continue;
    outletPoints = [
      ...outletPoints,
      {
        outletCode: nextId(outletPoints, (p) => p.outletCode),
        name,
        gisCode: null,
        aquatoolCode: null,
      },
    ];
    created++;
  }
  return { outletPoints, created };
}

function mergeAquifers(
  text: string | null,
  existing: Aquifer[],
  outletCodeByName: Map<string, number>
): { aquifers: Aquifer[]; updated: number; created: number } {
  if (text == null) return { aquifers: existing, updated: 0, created: 0 };
  let updated = 0;
  let created = 0;
  let aquifers = existing;
  for (const cols of parseSimpleCsv(text)) {
    const f = parseAquiferCsvFields(cols, outletCodeByName);
    if (!f.name) continue;
    const found = aquifers.find((a) => a.name === f.name);
    if (found) {
      updated++;
      aquifers = aquifers.map((a) =>
        a.aquiferCode === found.aquiferCode
          ? { ...a, vo: f.vo, alfa: f.alfa, outletCode: f.outletCode }
          : a
      );
    } else {
      created++;
      aquifers = [
        ...aquifers,
        {
          aquiferCode: nextId(aquifers, (a) => a.aquiferCode),
          name: f.name,
          gisCode: null,
          aquatoolCode: null,
          outletCode: f.outletCode,
          vo: f.vo,
          alfa: f.alfa,
          pumpingCol: 0,
        },
      ];
    }
  }
  return { aquifers, updated, created };
}

interface MergedZones {
  zones: Zone[];
  parameterValues: EvalHidProject["parameterValues"];
  stateVariableValues: EvalHidProject["stateVariableValues"];
  runoffZoneCodeByName: Map<string, number>;
  snowZoneCodeByName: Map<string, number>;
  updated: number;
  created: number;
  notices: string[];
}

async function mergeZones(
  t: Translate,
  folder: ReadableFolder,
  existingZones: Zone[],
  existingParameterValues: EvalHidProject["parameterValues"],
  existingStateVariableValues: EvalHidProject["stateVariableValues"]
): Promise<MergedZones> {
  let zones = existingZones;
  let parameterValues = existingParameterValues;
  let stateVariableValues = existingStateVariableValues;
  let updated = 0;
  let created = 0;
  const notices: string[] = [];
  const seenNames = new Set<string>(); // "runoff:Nombre" / "snow:Nombre", across every model file in this run

  for (const model of MODEL_TYPES) {
    const text = await readCsvText(folder, paramFileName(model.modelTypeName));
    if (text == null) continue;

    const { paramDefs, stateDefs } = zoneParamDefsFor(model);

    for (const cols of parseSimpleCsv(text)) {
      const name = cols[0];
      if (!name) continue;

      const seenKey = `${model.kind}:${name}`;
      if (seenNames.has(seenKey)) {
        notices.push(t("updateCsvProject.duplicateZoneAcrossFiles", { name }));
      }
      seenNames.add(seenKey);

      const found = zones.find((z) =>
        model.kind === 0
          ? z.modelTypeCode != null && z.name === name
          : z.snowModelTypeCode != null && z.name === name
      );
      const zoneCode = found ? found.zoneCode : nextId(zones, (z) => z.zoneCode);

      if (found) {
        updated++;
        zones = zones.map((z) =>
          z.zoneCode === zoneCode
            ? {
                ...z,
                modelTypeCode: model.kind === 0 ? model.modelTypeCode : z.modelTypeCode,
                snowModelTypeCode: model.kind === 1 ? model.modelTypeCode : z.snowModelTypeCode,
              }
            : z
        );
      } else {
        created++;
        zones = [
          ...zones,
          {
            zoneCode,
            name,
            modelTypeCode: model.kind === 0 ? model.modelTypeCode : null,
            snowModelTypeCode: model.kind === 1 ? model.modelTypeCode : null,
          },
        ];
      }

      const row = parseZoneParamRow(cols, zoneCode, model.kind, paramDefs, stateDefs);
      parameterValues = [
        ...parameterValues.filter((v) => !(v.zoneCode === zoneCode && v.kind === model.kind)),
        ...row.parameterValues,
      ];
      stateVariableValues = [
        ...stateVariableValues.filter((v) => !(v.zoneCode === zoneCode && v.kind === model.kind)),
        ...row.stateVariableValues,
      ];
    }
  }

  const runoffZoneCodeByName = new Map(
    zones.filter((z) => z.modelTypeCode != null).map((z) => [z.name, z.zoneCode])
  );
  const snowZoneCodeByName = new Map(
    zones.filter((z) => z.snowModelTypeCode != null).map((z) => [z.name, z.zoneCode])
  );

  return {
    zones,
    parameterValues,
    stateVariableValues,
    runoffZoneCodeByName,
    snowZoneCodeByName,
    updated,
    created,
    notices,
  };
}

function mergeSubBasins(
  t: Translate,
  text: string,
  existing: EvalHidProject["subBasins"],
  outletCodeByName: Map<string, number>,
  aquiferCodeByName: Map<string, number>,
  runoffZoneCodeByName: Map<string, number>,
  snowZoneCodeByName: Map<string, number>,
  zonesByCode: Map<number, Zone>
): { subBasins: EvalHidProject["subBasins"]; updated: number; notices: string[] } {
  const byName = new Map(existing.map((c) => [c.description, c]));
  let subBasins = existing;
  let updated = 0;
  const notices: string[] = [];
  const columnIndex = csvHeaderIndex(text);

  for (const cols of parseSimpleCsv(text)) {
    const f = parseSubBasinCsvFields(
      cols,
      columnIndex,
      outletCodeByName,
      aquiferCodeByName,
      runoffZoneCodeByName,
      snowZoneCodeByName,
      zonesByCode
    );
    const current = byName.get(f.name);
    if (!current) {
      notices.push(t("updateCsvProject.subBasinNotFound", { name: f.name }));
      continue;
    }

    if (f.outletUnresolvedName) {
      notices.push(
        t("updateCsvProject.unresolvedReference", {
          subBasin: f.name,
          kind: t("infoPopover.outletPoints"),
          name: f.outletUnresolvedName,
        })
      );
    }
    if (f.aquiferUnresolvedName) {
      notices.push(
        t("updateCsvProject.unresolvedReference", {
          subBasin: f.name,
          kind: t("infoPopover.aquifers"),
          name: f.aquiferUnresolvedName,
        })
      );
    }
    if (f.zoneUnresolvedName) {
      notices.push(
        t("updateCsvProject.unresolvedReference", {
          subBasin: f.name,
          kind: t("panels.zones"),
          name: f.zoneUnresolvedName,
        })
      );
    }
    if (f.snowZoneUnresolvedName) {
      notices.push(
        t("updateCsvProject.unresolvedReference", {
          subBasin: f.name,
          kind: t("canvas.snowZonesTab"),
          name: f.snowZoneUnresolvedName,
        })
      );
    }

    updated++;
    subBasins = subBasins.map((c) =>
      c.subBasinCode === current.subBasinCode
        ? {
            ...c,
            area: numberOr(f.areaRaw, c.area),
            precipAdjustment: numberOr(f.precipAdjustmentRaw, c.precipAdjustment),
            etpAdjustment: numberOr(f.etpAdjustmentRaw, c.etpAdjustment),
            tempAdjustment: numberOr(f.tempAdjustmentRaw, c.tempAdjustment),
            partialResults: numberOr(f.partialResultsRaw, c.partialResults),
            outletCode: f.outletCode || null,
            aquiferCode: f.aquiferCode,
            zoneCode: f.zoneCode,
            snowZoneCode: f.snowZoneCode,
            modelTypeCode: f.modelTypeCode,
            snowModelTypeCode: f.snowModelTypeCode,
          }
        : c
    );
  }

  return { subBasins, updated, notices };
}

/** Reads Ent_Config.csv, if present — updates the unit coefficient and the 2 aquifer options, leaving everything else in the project untouched. `configUpdated: false` when the file is missing (as opposed to importCsvProject.ts's from-scratch default). */
function mergeConfig(
  text: string | null,
  project: EvalHidProject
): {
  unitOptions: EvalHidProject["unitOptions"];
  untypedTables: EvalHidProject["untypedTables"];
  configUpdated: boolean;
} {
  if (text == null) {
    return {
      unitOptions: project.unitOptions,
      untypedTables: project.untypedTables,
      configUpdated: false,
    };
  }
  const values = parseConfigValues(text);
  const unitOptions = unitOptionsFromConfigValues(values, project.unitOptions);

  const untypedTables = new Map(project.untypedTables);
  const { negatives, pumping } = parseAquiferOptionFlags(values);
  if (negatives !== undefined || pumping !== undefined) {
    const otherRows = (project.untypedTables.get("APO_ACUIFEROS_OPC") ?? []).filter(
      (r) => Number(r.Codigo) !== 1 && Number(r.Codigo) !== 2
    );
    untypedTables.set("APO_ACUIFEROS_OPC", [
      ...otherRows,
      { Codigo: 1, Checked: negatives ?? false },
      { Codigo: 2, Checked: pumping ?? false },
    ]);
  }

  return { unitOptions, untypedTables, configUpdated: true };
}

/** Merges a folder of CSVs (see exportCsvProject.ts's format) into an already-open project, by name — see this file's own header for exactly what changes and what never does. Pure: returns a new project + a summary, doesn't touch React state or IndexedDB itself. */
export async function updateProjectFromCsvFolder(
  t: Translate,
  project: EvalHidProject,
  folder: ReadableFolder
): Promise<CsvUpdateResult> {
  const subBasinsText = await readCsvText(folder, DEFAULT_PATHS[LABEL_SUBBASINS]);
  if (subBasinsText == null) {
    throw new TranslatableError("importCsvProject.cuencasNotFoundError");
  }

  const notices: string[] = [];

  const outletPointsText = await readCsvText(folder, DEFAULT_PATHS[LABEL_OUTLET_POINTS]);
  const { outletPoints, created: outletsCreated } = mergeOutletPoints(
    outletPointsText,
    project.outletPoints
  );
  const outletCodeByName = new Map(outletPoints.map((p) => [p.name, p.outletCode]));

  const aquifersText = await readCsvText(folder, DEFAULT_PATHS[LABEL_AQUIFERS]);
  const {
    aquifers,
    updated: aquifersUpdated,
    created: aquifersCreated,
  } = mergeAquifers(aquifersText, project.aquifers, outletCodeByName);
  const aquiferCodeByName = new Map(aquifers.map((a) => [a.name, a.aquiferCode]));

  const {
    zones,
    parameterValues,
    stateVariableValues,
    runoffZoneCodeByName,
    snowZoneCodeByName,
    updated: zonesUpdated,
    created: zonesCreated,
    notices: zoneNotices,
  } = await mergeZones(
    t,
    folder,
    project.zones,
    project.parameterValues,
    project.stateVariableValues
  );
  notices.push(...zoneNotices);
  const zonesByCode = new Map(zones.map((z) => [z.zoneCode, z]));

  const {
    subBasins,
    updated: subBasinsUpdated,
    notices: subBasinNotices,
  } = mergeSubBasins(
    t,
    subBasinsText,
    project.subBasins,
    outletCodeByName,
    aquiferCodeByName,
    runoffZoneCodeByName,
    snowZoneCodeByName,
    zonesByCode
  );
  notices.push(...subBasinNotices);

  const configText = await readCsvText(folder, DEFAULT_PATHS[LABEL_CONFIG]);
  const { unitOptions, untypedTables, configUpdated } = mergeConfig(configText, project);

  const updatedProject: EvalHidProject = {
    ...project,
    subBasins,
    aquifers,
    outletPoints,
    zones,
    parameterValues,
    stateVariableValues,
    unitOptions,
    untypedTables,
  };

  return {
    project: updatedProject,
    summary: {
      subBasinsUpdated,
      zonesUpdated,
      zonesCreated,
      aquifersUpdated,
      aquifersCreated,
      outletsCreated,
      configUpdated,
      notices,
    },
  };
}
